import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ComplianceItemForm from '../components/ComplianceItemForm';
import {
  Plus, LayoutGrid, LayoutList, Rows3, FileText,
  ChevronLeft, ChevronRight, ShieldCheck, Check, Loader2, Upload,
  FolderOpen, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cleanTitle, levenshtein } from '../components/Kennisbank/constants';
import { FilterBar } from '../components/Kennisbank/FilterBar';
import { GridView } from '../components/Kennisbank/GridView';
import { ListView } from '../components/Kennisbank/ListView';
import { GroupedView } from '../components/Kennisbank/GroupedView';
import { FilesBrowser } from '../components/Files/FilesBrowser';
import { isPageAllowed } from '../lib/pageRights';

export default function Kennisbank() {
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('kennisbankViewMode') || 'grouped');
  const [scoresMap, setScoresMap] = useState({});
  const [duplicates, setDuplicates] = useState(new Set());

  const [showComplianceForm, setShowComplianceForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [groupBy, setGroupBy] = useState('location');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const { authAxios, paginas } = useAuth();
  const navigate = useNavigate();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const filters = {
    location: searchParams.get('location') || '',
    category: searchParams.get('category') || '',
    bv_id: searchParams.get('bv_id') || '',
    status: searchParams.get('status') || '',
    source: searchParams.get('source') || '',
    search: searchParams.get('search') || '',
    visibility: searchParams.get('visibility') || '',
    page: parseInt(searchParams.get('page') || '1'),
  };

  const setFilter = useCallback((key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const hasActiveFilters = filters.location || filters.category || filters.bv_id || filters.status || filters.source || filters.search || filters.visibility;

  // --- Tabs: "Kennis" (entries) | "Bestanden" (global files browser) ---
  // The active tab is driven by the URL ?tab= param (default 'kennis'). The
  // Bestanden tab is gated by the IDP 'bestanden' page-right; if a disallowed
  // user lands on ?tab=bestanden (or follows the /bestanden redirect) we fall
  // back to the Kennis tab and never render the files content.
  const filesAllowed = isPageAllowed(paginas, 'bestanden');
  const requestedTab = searchParams.get('tab');
  const activeTab = (requestedTab === 'bestanden' && filesAllowed) ? 'bestanden' : 'kennis';
  const setTab = useCallback((tab) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'bestanden') params.set('tab', 'bestanden');
    else params.delete('tab');
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    localStorage.setItem('kennisbankViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!entries || entries.length === 0) return;
    const dupes = new Set();
    for (let i = 0; i < entries.length; i++) {
      const a = cleanTitle(entries[i].title).toLowerCase().trim();
      for (let j = i + 1; j < entries.length; j++) {
        if (entries[i].location_id !== entries[j].location_id) continue;
        const b = cleanTitle(entries[j].title).toLowerCase().trim();
        if (a === b) {
          dupes.add(entries[i].entry_id); dupes.add(entries[j].entry_id); continue;
        }
        const aStart = a.substring(0, 30);
        const bStart = b.substring(0, 30);
        if (a.length >= 20 && b.length >= 20 && aStart === bStart) {
          dupes.add(entries[i].entry_id); dupes.add(entries[j].entry_id); continue;
        }
        if (a.length > 5 && b.length > 5 && levenshtein(a, b) < 5) {
          dupes.add(entries[i].entry_id); dupes.add(entries[j].entry_id);
        }
      }
    }
    setDuplicates(dupes);
  }, [entries]);

  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const [locRes, usersRes, scoresRes] = await Promise.all([
          api.get('/locations'),
          api.get('/users/list'),
          api.get('/compliance-scores').catch(() => ({ data: { scores: [] } }))
        ]);
        setLocations(locRes.data);
        setUsers(usersRes.data);
        const map = {};
        (scoresRes.data?.scores || []).forEach(s => { map[s.location_id] = s; });
        setScoresMap(map);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, [authAxios]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const api = authAxios();
        const params = new URLSearchParams();
        if (filters.location) params.set('location_id', filters.location);
        if (filters.category) params.set('category', filters.category);
        if (filters.bv_id) params.set('bv_id', filters.bv_id);
        if (filters.status) params.set('entry_status', filters.status);
        if (filters.source) params.set('source', filters.source);
        if (filters.search) params.set('search', filters.search);
        if (filters.visibility) params.set('visibility', filters.visibility);
        params.set('page', String(filters.page));
        params.set('limit', '24');

        const res = await api.get(`/entries?${params.toString()}`);
        setEntries(res.data.entries);
        setTotal(res.data.total);
        setPages(res.data.pages);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authAxios, filters.location, filters.category, filters.bv_id, filters.status, filters.source, filters.search, filters.visibility, filters.page]);

  const isPendingFilter = filters.status === 'pending_review';

  const handleBulkApprove = async () => {
    setBulkApproving(true);
    try {
      const api = authAxios();
      const res = await api.post('/entries/bulk-approve');
      const { approved, manual_review, total: t } = res.data;
      toast.success(`${approved} van ${t} items goedgekeurd${manual_review > 0 ? ` (${manual_review} items vereisen handmatige review)` : ''}`);
      const params = new URLSearchParams();
      if (filters.location) params.set('location_id', filters.location);
      if (filters.category) params.set('category', filters.category);
      if (filters.status) params.set('entry_status', filters.status);
      if (filters.search) params.set('search', filters.search);
      params.set('page', '1');
      params.set('limit', '24');
      const reloadRes = await api.get(`/entries?${params.toString()}`);
      setEntries(reloadRes.data.entries);
      setTotal(reloadRes.data.total);
      setPages(reloadRes.data.pages);
    } catch (e) {
      toast.error('Fout bij goedkeuren');
    } finally {
      setBulkApproving(false);
    }
  };

  return (
    <>
    <div className="space-y-4 max-w-[1400px]" data-testid="kennisbank-page">
      {/* Header: tabs + per-tab actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Tabs value={activeTab} onValueChange={setTab} className="w-full sm:w-auto">
            <TabsList className="grid grid-cols-2 w-full sm:inline-flex sm:w-auto" data-testid="kennisbank-tabs">
              <TabsTrigger value="kennis" data-testid="kennisbank-tab-kennis" className="gap-1.5 text-sm">
                <BookOpen className="w-3.5 h-3.5" /> Kennis
              </TabsTrigger>
              {filesAllowed && (
                <TabsTrigger value="bestanden" data-testid="kennisbank-tab-bestanden" className="gap-1.5 text-sm">
                  <FolderOpen className="w-3.5 h-3.5" /> Bestanden
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          {activeTab === 'kennis' && (
            <span className="hidden sm:inline text-xs text-slate-400 tabular-nums">{total} items</span>
          )}
        </div>

        {activeTab === 'kennis' && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center rounded-lg border border-slate-200 dark:border-[#30363D] p-0.5">
              <button data-testid="view-toggle-grid" onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-[#2E75B6] text-white' : 'text-slate-400 hover:text-slate-600'}`}>
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button data-testid="view-toggle-list" onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-[#2E75B6] text-white' : 'text-slate-400 hover:text-slate-600'}`}>
                <LayoutList className="w-4 h-4" />
              </button>
              <button data-testid="view-toggle-grouped" onClick={() => setViewMode('grouped')}
                className={`p-1.5 rounded-md transition-colors ${viewMode === 'grouped' ? 'bg-[#2E75B6] text-white' : 'text-slate-400 hover:text-slate-600'}`}
                title="Gegroepeerd">
                <Rows3 className="w-4 h-4" />
              </button>
            </div>
            <button data-testid="upload-document-button" onClick={() => navigate('/kennisbank/upload')}
              className="hidden sm:flex items-center gap-2 px-3.5 py-2 rounded-lg border border-[#2E75B6] text-[#2E75B6] text-sm font-medium hover:bg-[#2E75B6]/5 active:scale-[0.98] transition-[background-color,transform] duration-200">
              <Upload className="w-4 h-4" />
              <span className="hidden lg:inline">Upload Document</span>
            </button>
            <button data-testid="new-compliance-from-kennisbank" onClick={() => setShowComplianceForm(true)}
              className="hidden sm:flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-300 dark:border-[#30363D] text-slate-700 dark:text-[#C9D1D9] text-sm font-medium hover:bg-slate-50 dark:hover:bg-[#1C2128] active:scale-[0.98] transition-[background-color,transform] duration-200">
              <ShieldCheck className="w-4 h-4" />
              <span className="hidden lg:inline">Nieuw compliance item</span>
            </button>
            <button data-testid="new-entry-button" onClick={() => navigate('/kennisbank/nieuw')}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] active:scale-[0.98] transition-[background-color,transform] duration-200 shadow-sm">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nieuw Item</span>
            </button>
            {isPendingFilter && total > 0 && (
              <button data-testid="bulk-approve-kennisbank" onClick={handleBulkApprove} disabled={bulkApproving}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 disabled:opacity-50 transition-colors">
                {bulkApproving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Alles goedkeuren
              </button>
            )}
          </div>
        )}
      </div>

      {activeTab === 'kennis' ? (
        <>
          {/* Filters */}
          <FilterBar
            filters={filters}
            setFilter={setFilter}
            clearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            locations={locations}
            mobileFiltersOpen={mobileFiltersOpen}
            setMobileFiltersOpen={setMobileFiltersOpen}
          />

          {/* Content */}
          {loading ? (
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3' : 'space-y-2'}>
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-white rounded-xl border border-slate-200/80 p-4 animate-pulse">
                  <div className="h-4 w-3/4 bg-slate-100 rounded mb-3" />
                  <div className="h-3 w-1/2 bg-slate-100 rounded mb-2" />
                  <div className="h-3 w-full bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200/80 p-12 text-center">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Geen items gevonden. Pas filters aan of voeg een item toe.</p>
              <button onClick={() => navigate('/kennisbank/nieuw')}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-sm hover:bg-[#27649C] transition-colors">
                <Plus className="w-4 h-4" />
                Nieuw Item
              </button>
            </div>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <GridView entries={entries} duplicates={duplicates} navigate={navigate} />
              ) : viewMode === 'list' ? (
                <ListView entries={entries} duplicates={duplicates} navigate={navigate} />
              ) : (
                <GroupedView
                  entries={entries}
                  scoresMap={scoresMap}
                  duplicates={duplicates}
                  groupBy={groupBy}
                  setGroupBy={setGroupBy}
                  collapsedGroups={collapsedGroups}
                  setCollapsedGroups={setCollapsedGroups}
                  navigate={navigate}
                  locations={locations}
                />
              )}

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <button disabled={filters.page <= 1} onClick={() => setFilter('page', String(filters.page - 1))}
                    className="p-2 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 disabled:opacity-30 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-500">Pagina {filters.page} van {pages}</span>
                  <button disabled={filters.page >= pages} onClick={() => setFilter('page', String(filters.page + 1))}
                    className="p-2 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 disabled:opacity-30 transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <FilesBrowser paramPrefix="f_" showTitle={false} />
      )}
    </div>

    <ComplianceItemForm
      open={showComplianceForm}
      onOpenChange={setShowComplianceForm}
      item={null}
      locations={locations}
      users={users}
      onSuccess={() => setShowComplianceForm(false)}
    />
    </>
  );
}
